import {
  Alert,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import TitleWithBack from "../../custom/TitleWithBack";
import { Installation } from "../../../api/requests/installationsService";
import ButtonConfItem from "../../custom/ButtonConfItem";
import ButtonItem from "../../custom/ButtonItem";
import { primaryType } from "../../../costants/costants";
import { CalibrationType, actuator, plant, pump } from "./costant";
import FormSelect from "../../custom/select/FormSelect";
import { getAllDevices } from "../../../api/services/deviceService";
import { getJob, setJob } from "../../../api/services/installationsService";
import { useHistory } from "react-router";
import { useSnackbar } from "notistack";
import CustomLoading from "../../custom/CustomLoading";

type CalibrationProps = {
  installation_id: string;
};

type CalibrationSetStatus = "failed" | "pending" | "done" | null;

const Calibration: React.FC<CalibrationProps> = ({ installation_id }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [setParamsConfig, setSetParamsConfig] = useState<Record<string, any>>(
    {}
  );
  const [lastSetStatus, setLastSetStatus] =
    useState<CalibrationSetStatus>(null);
  const normalizeConfig = (raw: any): Record<string, any> => {
    if (!raw) return {};
    let cfg = raw;
    if (typeof cfg === "string") {
      try {
        cfg = JSON.parse(cfg);
      } catch {
        return {};
      }
    }
    if (cfg && typeof cfg === "object" && "config" in cfg) {
      const inner = (cfg as any).config;
      if (typeof inner === "string") {
        try {
          const parsed = JSON.parse(inner);
          return parsed;
        } catch {
          return {};
        }
      }
      if (inner && typeof inner === "object") {
        return inner;
      }
    }
    if (cfg && typeof cfg === "object") {
      return cfg as Record<string, any>;
    }
    return {};
  };

  const [loading, setLoading] = useState<boolean>(true);
  const [selectedInstallation, setSelectedInstallation] =
    useState<Installation | null>(null);
  useEffect(() => {
    getAllDevices().then((res: any) => {
      if (res && res.installations) {
        const tmp = res.installations.filter(
          (i: any) => i.id === installation_id
        );
        if (tmp && tmp.length > 0) {
          setSelectedInstallation(tmp[0]);
          getJob(installation_id, "set_params")
            .then((jobRes: any) => {
              const config = normalizeConfig(
                jobRes && jobRes.calibration ? jobRes.calibration.config : {}
              );
              const status =
                jobRes &&
                jobRes.calibration &&
                typeof jobRes.calibration.last_set_status === "string"
                  ? jobRes.calibration.last_set_status
                  : null;
              setSetParamsConfig(config);
              setLastSetStatus(
                status === "failed" || status === "pending" || status === "done"
                  ? status
                  : null
              );
              setLoading(false);
            })
            .catch(() => {
              setSetParamsConfig({});
              setLastSetStatus(null);
              setLoading(false);
            });
        }
      }
    });
  }, []);

  const [calibrationsConfiguration, setCalibrationsConfiguration] = useState<
    any[]
  >([]);
  const updateFieldValue = (calibrationIndex: number, fieldIndex: number, value: any) => {
    setCalibrationsConfiguration((prev) =>
      prev.map((calibration, cIdx) => {
        if (cIdx !== calibrationIndex) return calibration;
        return {
          ...calibration,
          field: calibration.field.map((field: any, fIdx: number) =>
            fIdx === fieldIndex ? { ...field, value } : field
          ),
        };
      })
    );
  };
  useEffect(() => {
    const applyConfig = (items: CalibrationType[]) => {
      const isNumericValue = (value: any) =>
        value !== "" &&
        value !== null &&
        value !== undefined &&
        typeof value !== "boolean" &&
        !Number.isNaN(Number(value));
      const getAliasKeys = (baseName: string) => {
        switch (baseName) {
          case "wdir":
            return ["wdir", "w_dir"];
          case "wvel":
            return ["wvel", "w_spd"];
          case "ppref":
            return ["ppref", "p_pre"];
          case "ppostf":
            return ["ppostf", "pposf", "p_pos"];
          case "flvl":
            return ["flvl", "fert"];
          case "text":
            return ["text", "t_ext"];
          case "coil":
            return ["coil", "curr"];
          case "patm":
            return ["patm", "pint"];
          case "ev":
            return ["ev", "ev_in"];
          case "batt":
            return ["batt", "bat"];
          case "solar":
            return ["solar", "sol"];
          default:
            return [baseName];
        }
      };
      const readFirstDefined = (keys: string[]) => {
        for (const key of keys) {
          const value = setParamsConfig?.[key];
          if (value !== undefined && value !== null) return value;
        }
        return undefined;
      };
      const getSuffix = (name: string) => {
        if (name.endsWith("min")) return "min";
        if (name.endsWith("max")) return "max";
        if (name.endsWith("off")) return "off";
        return null;
      };
      const getGroupName = (name: string) => {
        const suffix = getSuffix(name);
        if (!suffix) return name;
        return name.slice(0, -suffix.length);
      };
      const readGroupedNumeric = (name: string) => {
        const suffix = getSuffix(name);
        const groupName = getGroupName(name);
        if (!suffix) return undefined;
        const groupKeys = getAliasKeys(groupName);
        for (const key of groupKeys) {
          const groupValue = setParamsConfig?.[key];
          if (groupValue && typeof groupValue === "object") {
            const nested = (groupValue as any)[suffix];
            if (isNumericValue(nested)) return nested;
          }
          if (suffix === "off" && isNumericValue(groupValue)) {
            return groupValue;
          }
        }
        return undefined;
      };
      const readBmeValue = (baseName: string) => {
        const bme = setParamsConfig?.bme;
        if (!bme || typeof bme !== "object") return undefined;
        if (baseName === "tint" && isNumericValue(bme.t)) return bme.t;
        if (baseName === "hint" && isNumericValue(bme.h)) return bme.h;
        if (baseName === "patm" && isNumericValue(bme.p)) return bme.p;
        return undefined;
      };
      const readAliasValue = (baseName: string) => {
        const aliasValue = readFirstDefined(getAliasKeys(baseName));
        if (isNumericValue(aliasValue)) return aliasValue;
        return undefined;
      };
      const hasNumericGroup = (value: any) =>
        value &&
        typeof value === "object" &&
        (isNumericValue((value as any).min) ||
          isNumericValue((value as any).max) ||
          isNumericValue((value as any).off));

      return items.map((calibration: CalibrationType) => ({
        ...calibration,
        field: calibration.field.map((f: any) => {
          const fallbackValue = f.value;
          const baseName = f.name.endsWith("off") ? f.name.slice(0, -3) : f.name;
          const configValue = readFirstDefined(getAliasKeys(f.name));
          const baseConfigValue = readFirstDefined(getAliasKeys(baseName));
          if (f.type === "switch") {
            const enabledValue = readFirstDefined(
              getAliasKeys(f.name).flatMap((key) => [
                `${key}_enabled`,
                `${key}_en`,
              ])
            );
            if (typeof enabledValue === "boolean") {
              return { ...f, value: enabledValue };
            }
            if (enabledValue !== undefined && enabledValue !== null) {
              return { ...f, value: Boolean(Number(enabledValue)) };
            }
            if (hasNumericGroup(configValue) || hasNumericGroup(baseConfigValue)) {
              return { ...f, value: true };
            }
            if (typeof configValue === "boolean") {
              return { ...f, value: configValue };
            }
            if (f.name === "ev") {
              return { ...f, value: Number(configValue) === 1 };
            }
            if (configValue !== undefined && configValue !== null) {
              return { ...f, value: Number(configValue) !== 0 };
            }
            return { ...f, value: Boolean(fallbackValue) };
          }

          if (f.type === "number") {
            const groupedValue = readGroupedNumeric(f.name);
            if (isNumericValue(groupedValue)) {
              return { ...f, value: groupedValue };
            }
            const bmeValue = readBmeValue(baseName);
            if (isNumericValue(bmeValue)) {
              return { ...f, value: bmeValue };
            }
            const aliasValue = readAliasValue(baseName);
            if (isNumericValue(aliasValue)) {
              return { ...f, value: aliasValue };
            }
            const valueFromConfig =
              configValue !== undefined && configValue !== null
                ? configValue
                : baseConfigValue;
            if (isNumericValue(valueFromConfig)) {
              return { ...f, value: valueFromConfig };
            }
            return { ...f, value: fallbackValue ?? "" };
          }

          return f;
        }),
      }));
    };

    if (selectedInstallation) {
      if (selectedInstallation.model === "actuator") {
        setCalibrationsConfiguration(applyConfig(actuator(t)));
      }
      if (selectedInstallation.model === "plant") {
        setCalibrationsConfiguration(applyConfig(plant(t)));
      }
      if (selectedInstallation.model === "pump") {
        setCalibrationsConfiguration(applyConfig(pump(t)));
      }
    }
  }, [selectedInstallation, setParamsConfig]);

  const handleSubmit = () => {
    let tmpRes: any = {};
    const enabledFlags = (res: Record<string, any>) =>
      Object.keys(res).reduce((acc: Record<string, boolean>, key: string) => {
        if (typeof res?.[key]?.enabled === "boolean") {
          acc[`${key}_enabled`] = res[key].enabled;
        }
        return acc;
      }, {});
    calibrationsConfiguration.forEach((a: CalibrationType) => {
      let tmp = {};
      a.field.forEach((f: any) => {
        if (f.type === "switch") {
          tmp = { ...tmp, enabled: f.value };
        } else {
          if (f.name.endsWith("min")) {
            tmp = { ...tmp, min: f.value };
          }
          if (f.name.endsWith("max")) {
            tmp = { ...tmp, max: f.value };
          }
          if (f.name.endsWith("off")) {
            tmp = { ...tmp, offset: f.value };
          }
        }
      });
      tmpRes = { ...tmpRes, [a.field[0].name]: tmp };
    });
    if (selectedInstallation && selectedInstallation.model === "actuator") {
      const body = {
        bme: {
          t: Number(tmpRes.tint.offset),
          h: Number(tmpRes.hint.offset),
          p: Number(tmpRes.patm.offset),
        },
        ...enabledFlags(tmpRes),
      };
      setJob(installation_id, selectedInstallation?.model || "", {
        calibrations: body,
      }).then((res) => {
        if (res && !res.err) {
          enqueueSnackbar(t("calibrationSendSuccess"), {
            variant: "success",
          });
          history.goBack();
        } else {
          enqueueSnackbar(t("calibrationSendError"), { variant: "error" });
        }
      });
    }
    if (selectedInstallation && selectedInstallation.model === "plant") {
      const body = {
        sm3: [
          {
            t: Number(tmpRes.tsoil1.offset),
            h: Number(tmpRes.hsoil1.offset),
            ec: Number(tmpRes.ecsoil1.offset),
          }, //offset valori pianta 1
          {
            t: Number(tmpRes.tsoil2.offset),
            h: Number(tmpRes.hsoil2.offset),
            ec: Number(tmpRes.ecsoil2.offset),
          }, //offset valori pianta 2
          {
            t: Number(tmpRes.tsoil3.offset),
            h: Number(tmpRes.hsoil3.offset),
            ec: Number(tmpRes.ecsoil3.offset),
          }, //offset valori pianta 3
          {
            t: Number(tmpRes.tsoil4.offset),
            h: Number(tmpRes.hsoil4.offset),
            ec: Number(tmpRes.ecsoil4.offset),
          }, //offset valori pianta 4
        ],
        bat: Number(tmpRes.batt.offset), //offset tensione batteria
        sol: Number(tmpRes.solar.offset), //offset tensione pannello solare
        ev_in: tmpRes.ev.enabled ? 1 : 0, //segnale elettrovalvola
        bme: {
          t: Number(tmpRes.tint.offset),
          h: Number(tmpRes.hint.offset),
          p: Number(tmpRes.patm.offset),
        }, // offset temperatura umidità pressione
        ...enabledFlags(tmpRes),
      };
      setJob(installation_id, selectedInstallation?.model || "", {
        calibrations: body,
      }).then((res) => {
        if (res && !res.err) {
          enqueueSnackbar(t("calibrationSendSuccess"), {
            variant: "success",
          });
          history.goBack();
        } else {
          enqueueSnackbar(t("calibrationSendError"), { variant: "error" });
        }
      });
    }
    if (selectedInstallation && selectedInstallation.model === "pump") {
      const body = {
        w_dir: {
          min: Number(tmpRes.wdir.min),
          max: Number(tmpRes.wdir.max),
          off: Number(tmpRes.wdir.offset),
        },
        w_spd: {
          min: Number(tmpRes.wvel.min),
          max: Number(tmpRes.wvel.max),
          off: Number(tmpRes.wvel.offset),
        },
        p_pre: {
          min: Number(tmpRes.ppref.min),
          max: Number(tmpRes.ppref.max),
          off: Number(tmpRes.ppref.offset),
        },
        p_pos: {
          min: Number(tmpRes.ppostf.min),
          max: Number(tmpRes.ppostf.max),
          off: Number(tmpRes.ppostf.offset),
        },
        fert: {
          min: Number(tmpRes.flvl.min),
          max: Number(tmpRes.flvl.max),
          off: Number(tmpRes.flvl.offset),
        },
        t_ext: Number(tmpRes.text.offset),
        curr: Number(tmpRes.coil.offset),
        bme: {
          t: Number(tmpRes.tint.offset),
          h: Number(tmpRes.hint.offset),
          p: Number(tmpRes.patm.offset),
        },
        ...enabledFlags(tmpRes),
      };
      setJob(installation_id, selectedInstallation?.model || "", {
        calibrations: body,
      }).then((res) => {
        if (res && !res.err) {
          enqueueSnackbar(t("calibrationSendSuccess"), {
            variant: "success",
          });
          history.goBack();
        } else {
          enqueueSnackbar(t("calibrationSendError"), { variant: "error" });
        }
      });
    }
  };

  if (loading) {
    return (<CustomLoading />);
  }

  const calibrationStatusMap: Record<
    Exclude<CalibrationSetStatus, null>,
    { severity: "error" | "warning" | "success"; message: string }
  > = {
    failed: {
      severity: "error",
      message: t("calibrationLastSetFailed"),
    },
    pending: {
      severity: "warning",
      message: t("calibrationLastSetPending"),
    },
    done: {
      severity: "success",
      message: t("calibrationLastSetDone"),
    },
  };

  const calibrationStatusAlert = lastSetStatus
    ? calibrationStatusMap[lastSetStatus]
    : null;

  return (
    <>
      <TitleWithBack title={t("calibration")} />
      <Grid container spacing={2} justifyContent="center">
        <Grid item xs={12} md={8}>
          {calibrationStatusAlert && (
            <Alert severity={calibrationStatusAlert.severity} sx={{ mb: 2 }}>
              {calibrationStatusAlert.message}
            </Alert>
          )}
          <Card variant="outlined">
            <CardContent>
              <Grid container spacing={2}>
                {calibrationsConfiguration.map((a: CalibrationType, calibrationIndex: number) => {
                  return (
                    <Grid
                      item
                      container
                      spacing={2}
                      xs={12}
                      justifyContent="space-between"
                    >
                      <Grid item xs={12}>
                        <Typography variant="h6">{a.label}</Typography>
                      </Grid>
                      {a.field.map((f: any, fieldIndex: number) => {
                        if (f.type === "switch") {
                          return (
                            <Grid item container xs={2} alignItems="center">
                              <Grid item>
                                <Switch
                                  checked={Boolean(f.value)}
                                  onChange={(e) =>
                                    updateFieldValue(
                                      calibrationIndex,
                                      fieldIndex,
                                      e.target.checked
                                    )
                                  }
                                />
                              </Grid>
                              <Grid item>
                                <Typography variant="body1">
                                  {t("enabled")}
                                </Typography>
                              </Grid>
                            </Grid>
                          );
                        }
                        if (f.type === "number") {
                          return (
                            <Grid item xs={6}>
                              <TextField
                                size="small"
                                fullWidth
                                type="number"
                                label={f.label}
                                variant="outlined"
                                value={f.value ?? ""}
                                onChange={(e) =>
                                  updateFieldValue(
                                    calibrationIndex,
                                    fieldIndex,
                                    e.target.value
                                  )
                                }
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      {f.endAdornment ? f.endAdornment : ""}
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Grid>
                          );
                        }
                        if (f.type === "select") {
                          return (
                            <Grid item xs={6}>
                              <FormSelect
                                placeholder={f.label}
                                keyValue={f.name}
                                name={f.name}
                                value={f.value ?? ""}
                                onChange={(e: any) =>
                                  updateFieldValue(
                                    calibrationIndex,
                                    fieldIndex,
                                    e.target.value
                                  )
                                }
                                options={f.options ? f.options : []}
                              />
                            </Grid>
                          );
                        }
                      })}
                    </Grid>
                  );
                })}
                <Grid
                  item
                  container
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Grid item>
                    <ButtonConfItem
                      buttonLabel={t("reset")}
                      buttonOnConfirm={() => {
                        console.log("reset");
                      }}
                      questionLabel={t("question")}
                    />
                  </Grid>
                  <Grid item>
                    <ButtonItem
                      buttonType={primaryType}
                      label={t("ok")}
                      buttonOnClick={handleSubmit}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

export default Calibration;
